import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { EmptyState, Page, StatusBadge } from "../../../components/ui";
import {
  DetailHeader,
  DetailSkeleton,
  FactList,
  MediaPlayer,
  TagList,
} from "../../../components/detail";
import { getAdDetails } from "../../../services/ads";
import { getDate } from "../../../utils/format";
import { capitalize, stripHtml } from "../../../utils/list";
import { formatNumber } from "../../../utils/numbers";

/** Clicks per view, as a percentage with 2 decimals. */
export const clickThroughRate = (views, clicks) =>
  Number(views) > 0
    ? `${((Number(clicks) / Number(views)) * 100).toFixed(2)} %`
    : "—";

const ViewAds = () => {
  const { ad_id } = useParams();
  const { t } = useTranslation();
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getAdDetails(ad_id)
      .then((data) => setAd(data?.savedAds || null))
      .catch((error) => console.error("Error fetching data:", error))
      .finally(() => setLoading(false));
  }, [ad_id]);

  if (loading) {
    return (
      <Page>
        <DetailSkeleton />
      </Page>
    );
  }

  if (!ad) {
    return (
      <Page>
        <DetailHeader title={t("Ad details")} />
        <EmptyState>{t("No Data Found")}</EmptyState>
      </Page>
    );
  }

  const stats = [
    { label: t("Ad Views"), value: formatNumber(ad.ads_view ?? 0), icon: "ri-eye-line", color: "blue" },
    { label: t("Ad Clicks"), value: formatNumber(ad.ads_click ?? 0), icon: "ri-cursor-line", color: "green" },
    {
      label: t("Click-through rate"),
      value: clickThroughRate(ad.ads_view, ad.ads_click),
      icon: "ri-percent-line",
      color: "amber",
    },
  ];

  return (
    <Page>
      <DetailHeader
        title={ad.title}
        badges={<StatusBadge active={Boolean(ad.status)} />}
      />

      <div className="detail-stats">
        {stats.map((stat) => (
          <div key={stat.label} className="detail-metric detail-stat">
            <span
              className="detail-metric-icon"
              style={{ "--metric-color": `var(--ui-${stat.color})` }}
              aria-hidden="true"
            >
              <i className={stat.icon}></i>
            </span>
            <div style={{ minWidth: 0 }}>
              <p className="detail-metric-label">{stat.label}</p>
              <p className="detail-metric-value">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="detail-layout">
        <div className="detail-stack">
          {ad.banner_url && (
            <section className="ui-card detail-pad">
              <h3 className="detail-section-title">{t("Banner")}</h3>
              <img className="detail-banner" src={ad.banner_url} alt="" />
            </section>
          )}
          {ad.media_id && <MediaPlayer mediaId={ad.media_id} />}
          <section className="ui-card detail-pad">
            <div className="detail-section">
              <h3 className="detail-section-title">{t("desc")}</h3>
              <p className="detail-description">
                {stripHtml(ad.description) || "—"}
              </p>
            </div>
          </section>
        </div>

        <section className="ui-card detail-pad" style={{ alignSelf: "start" }}>
          <FactList
            plain
            items={[
              { label: t("Autoplay"), value: capitalize(ad.autoplay) },
              { label: t("Language"), value: capitalize(ad.default_language) },
              { label: t("Expiry Date"), value: getDate(ad.expiry_date) },
              {
                label: t("Redirect URL"),
                value: ad.redirect_url ? (
                  <a
                    href={ad.redirect_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="detail-link"
                  >
                    {ad.redirect_url}
                  </a>
                ) : undefined,
              },
            ]}
          />
          <div style={{ marginTop: 20 }}>
            <TagList label={t("Ads Placement")} items={ad.placement} />
          </div>
        </section>
      </div>
    </Page>
  );
};

export default React.memo(ViewAds);
